import { ShiftBatRowType } from '../model/ShiftBatRowType';
import { toHrsMinsSecs, toTime } from '../libs/formatLib';
import { find } from 'lodash/collection';
import { getSecondsSinceMidnightAsDayjs } from '../libs/formatLib';


/**
 * Validation utilities for ShiftBat rows
 * These functions provide the same logic as the useShiftBatValidation hook
 * but can be used by the ShiftBat class without React dependencies
 */

/**
 * Base validation functions for ShiftBatRow
 */

/**
 * Check if the row has a valid title
 * @param {Object} shiftBatRow - The shift bat row
 * @returns {boolean} True if title is valid
 */
export const isValidTitle = (shiftBatRow) => {
    return ![ShiftBatRowType.note, ShiftBatRowType.noteTimed].includes(shiftBatRow.type) || !!shiftBatRow.title?.length;
};

/**
 * Check if the row has a valid time
 * @param {Object} shiftBatRow - The shift bat row
 * @returns {boolean} True if time is valid
 */
export const isValidTime = (shiftBatRow) => {
    if (shiftBatRow.hideTime || [ShiftBatRowType.dead, ShiftBatRowType.service, ShiftBatRowType.note].includes(shiftBatRow.type)) {
        return true;
    }
    return Number.isFinite(shiftBatRow.time) && shiftBatRow.time >= 0;
};

/**
 * Check if the row has a valid sequence (time is after previous row's end time)
 * @param {Object} shiftBatRow - The shift bat row
 * @param {number} rowIndex - The index of the current row
 * @returns {boolean} True if sequence is valid
 */
export const isValidSequence = (shiftBatRow, rowIndex) => {
    if (!Number.isFinite(rowIndex) || rowIndex <= 0 || [ShiftBatRowType.dead, ShiftBatRowType.service, ShiftBatRowType.note].includes(shiftBatRow.type)) {
        return true;
    }
    // gets previous timing row
    const getPreviousTimingRow = (rowIndex, rows) => {
      if (rowIndex <= 0) return null;
      
      const prevIndex = rowIndex - 1;
      const prevRow = rows[prevIndex];
      
      if (!prevRow) return null;
      
      if (prevRow.hideTime === true) {
        return getPreviousTimingRow(prevIndex, rows);
      }
      
      return prevRow;
    };
    
    const previousTimingRow = getPreviousTimingRow(rowIndex, shiftBatRow.shiftBat?.rows || []);
    if(!previousTimingRow) return true;
    const prevEndTime = previousTimingRow?.getEndTime?.();
    return (shiftBatRow.getTime() - prevEndTime) >= 0;
};

/**
 * Check if the row is loaded (bypass validation if not loaded)
 * @param {Object} shiftBatRow - The shift bat row
 * @returns {boolean} True if validation should be bypassed
 */
export const isLoadedBypass = (shiftBatRow) => {
    return !shiftBatRow.loaded;
};

/**
 * Main validation function for ShiftBatRow
 * @param {Object} shiftBatRow - The shift bat row
 * @param {number} rowIndex - The index of the current row
 * @returns {boolean} True if row is valid
 */
export const isValid = (shiftBatRow, rowIndex) => {
    console.log('ShiftBatRow isValid: ', shiftBatRow.title);
    if (isLoadedBypass(shiftBatRow)) {
        console.log('ShiftBatRow VALID: not loaded');
        return true;
    }
    if (!isValidTitle(shiftBatRow)) {
        console.log('ShiftBatRow INVALID: no title');
        return false;
    }
    if (!isValidTime(shiftBatRow)) {
        console.log('ShiftBatRow INVALID: invalid time');
        return false;
    }
    if (!isValidSequence(shiftBatRow, rowIndex)) {
        console.log('ShiftBatRow INVALID: time is before the end of ' + shiftBatRow.shiftBat.rows[rowIndex - 1].title);
        console.log('ShiftBatRow INVALID: time: ', shiftBatRow.getTime());
        console.log('ShiftBatRow INVALID: endTime: ', shiftBatRow.shiftBat.rows[rowIndex - 1].getEndTime());
        return false;
    }
    console.log('ShiftBatRow VALID');
    return true;
};

/**
 * Get the reason why a row is invalid
 * @param {Object} shiftBatRow - The shift bat row
 * @param {number} rowIndex - The index of the current row
 * @param {Array} excludedFields - Fields to exclude from validation
 * @returns {string} Reason for invalidity or undefined if valid
 */
export const invalidReason = (shiftBatRow, rowIndex) => {
    if (!isValidTitle(shiftBatRow)) {
        return 'Missing title';
    }
    if (!isValidTime(shiftBatRow)) {
        return 'Invalid time';
    }
    if (!isValidSequence(shiftBatRow, rowIndex)) {
        return 'Time too early';
    }
};

/**
 * Get a message about what's missing from the row
 * @param {Object} shiftBatRow - The shift bat row
 * @returns {string|null} Missing fields message or null
 */
export const getInvalidMessage = (shiftBatRow) => {
    const missing = [];
    if (!isValidTitle(shiftBatRow)) missing.push('title');
    // Only check time for rows that require timing (exclude untimed notes)
    if (!isValidTime(shiftBatRow)) {
        missing.push('time');
    }
    return missing?.length ? `Missing ${missing.join(', ')}` : null;
};

/**
 * Check if the row is in default state
 * @param {Object} shiftBatRow - The shift bat row
 * @returns {boolean} True if row is in default state
 */
export const isDefault = (shiftBatRow) => {
    return !shiftBatRow.title?.length && !shiftBatRow.description?.length && (!shiftBatRow.priority?.length || shiftBatRow.priority === 'Normal');
};

/**
 * Validation functions for ShiftBatServiceRow
 */

/**
 * Check if the service row has a valid route
 * @param {Object} shiftBatRow - The service row
 * @param {Object} allRoutes - All available routes
 * @returns {boolean} True if route is valid
 */
export const isValidRoute = (shiftBatRow, {allRoutes}) => {
    console.log('isValidRoute: ', shiftBatRow.routeId, shiftBatRow.route);
    const route = shiftBatRow.route || allRoutes?.[shiftBatRow.routeId];
    return !!route;
};

/**
 * Check if the service row has a valid trip
 * @param {Object} shiftBatRow - The service row
 * @param {Object} allRoutes - All available routes
 * @returns {boolean} True if trip is valid
 */
export const isValidTrip = (shiftBatRow, {allRoutes}) => {
    console.log('isValidTrip: ', shiftBatRow, shiftBatRow.tripId, shiftBatRow.trip);
    const route = shiftBatRow.route || allRoutes?.[shiftBatRow.routeId];
    if (!route) return false;
    const trip = find(route.services, {tripId: (shiftBatRow.trip?.tripId || shiftBatRow.tripId)});
    if(!trip) return false;
    return true;
};

/**
 * Check if the service row has a valid sequence
 * @param {Object} shiftBatRow - The service row
 * @param {number} rowIndex - The index of the current row
 * @returns {boolean} True if sequence is valid
 */
export const isValidServiceSequence = (shiftBatRow, rowIndex) => {
    if (!Number.isFinite(rowIndex) || rowIndex <= 0) {
        return true;
    }
    const prevEndTime = shiftBatRow.shiftBat?.rows?.[rowIndex - 1]?.getEndTime?.();
    return (shiftBatRow.getEndTime() - prevEndTime) >= -60;
};

/**
 * Main validation function for ShiftBatServiceRow
 * @param {Object} shiftBatRow - The service row
 * @param {Object} allRoutes - All available routes
 * @param {number} rowIndex - The index of the current row
 * @returns {boolean} True if service row is valid
 */
export const isValidService = (shiftBatRow, {allRoutes}, rowIndex) => {
    if (!shiftBatRow.loaded) {
        return true;
    }
    if (!isValidRoute(shiftBatRow, {allRoutes})) {
        return false;
    }
    if (!isValidTrip(shiftBatRow, {allRoutes})) {
        return false;
    }
    if (!shiftBatRow.isValidSplitStops?.({allRoutes})) {
        return false;
    }
    return isValid(shiftBatRow, rowIndex);
};

/**
 * Get the reason why a service row is invalid
 * @param {Object} shiftBatRow - The service row
 * @param {Object} allRoutes - All available routes
 * @param {number} rowIndex - The index of the current row
 * @returns {string} Reason for invalidity
 */
export const invalidReasonService = (shiftBatRow, {allRoutes}, rowIndex) => {
    // First check basic validation - if that fails, return that reason
    const basicReason = invalidReason(shiftBatRow, rowIndex);
    if (basicReason) {
        return basicReason;
    }
    
    // Only check service-specific issues if basic validation passes
    const route = allRoutes[shiftBatRow.routeId || shiftBatRow.route?.routeId];
    if (!route) {
        return 'Route deleted or invalid';
    }
    const trip = find(route.services, {tripId: (shiftBatRow.tripId || shiftBatRow.trip?.tripId)});
    if (!trip) {
        return 'Service deleted or invalid';
    }
    
    // Check for invalid split stops
    if (shiftBatRow.isValidSplitStops && !shiftBatRow.isValidSplitStops({allRoutes})) {
        const invalidStops = [];
        if (shiftBatRow.startStopTimeId && !trip.stopTimes?.some(st => st.stopTimeId === shiftBatRow.startStopTimeId)) {
            invalidStops.push('start stop');
        }
        if (shiftBatRow.endStopTimeId && !trip.stopTimes?.some(st => st.stopTimeId === shiftBatRow.endStopTimeId)) {
            invalidStops.push('end stop');
        }
        if (invalidStops.length > 0) {
            return `Split stops invalid: ${invalidStops.join(' and ')} no longer exists in this trip`;
        }
    }
    
    return undefined; // No issues found
};

/**
 * Get invalid message for service row
 * @param {Object} shiftBatRow - The service row
 * @returns {string|null} Missing fields message or null
 */
export const getInvalidMessageService = (shiftBatRow) => {
    const missing = [];
    if (!shiftBatRow.route) missing.push('route');
    if (!shiftBatRow.trip) missing.push('trip');
    if (shiftBatRow.time < 0) missing.push('time');
    return missing?.length ? `Missing ${missing.join(', ')}` : null;
};

/**
 * Check if service row is in default state
 * @param {Object} shiftBatRow - The service row
 * @returns {boolean} True if row is in default state
 */
export const isDefaultService = (shiftBatRow) => {
    return isDefault(shiftBatRow) && !shiftBatRow.route && !shiftBatRow.trip;
};

/**
 * Validation functions for ShiftBatStopRow
 */

/**
 * Check if the stop row has a valid stop
 * @param {Object} shiftBatRow - The stop row
 * @param {Object} allStops - All available stops
 * @returns {boolean} True if stop is valid
 */
export const hasValidStop = (shiftBatRow, {allStops}) => {
    return (!shiftBatRow.loaded || shiftBatRow.stop || allStops[shiftBatRow.stopId || shiftBatRow.stop?.stopId]);
};

/**
 * Main validation function for ShiftBatStopRow
 * @param {Object} shiftBatRow - The stop row
 * @param {Object} allStops - All available stops
 * @param {number} rowIndex - The index of the current row
 * @returns {boolean} True if stop row is valid
 */
export const isValidStop = (shiftBatRow, {allStops}, rowIndex) => {
    return hasValidStop(shiftBatRow, {allStops}) && isValid(shiftBatRow, rowIndex);
};

/**
 * Get the reason why a stop row is invalid
 * @param {Object} shiftBatRow - The stop row
 * @param {Object} allStops - All available stops
 * @param {number} rowIndex - The index of the current row
 * @returns {string} Reason for invalidity
 */
export const invalidReasonStop = (shiftBatRow, {allStops}, rowIndex) => {
    // First check basic validation - if that fails, return that reason
    const basicReason = invalidReason(shiftBatRow, rowIndex);
    if (basicReason) {
        return basicReason;
    }
    
    // Only check stop-specific issues if basic validation passes
    if (!allStops[shiftBatRow.stopId || shiftBatRow.stop?.stopId]) {
        return 'Stop deleted or invalid';
    }
    
    return undefined; // No issues found
};

/**
 * Get invalid message for stop row
 * @param {Object} shiftBatRow - The stop row
 * @returns {string|null} Missing fields message or null
 */
export const getInvalidMessageStop = (shiftBatRow) => {
    const missing = [];
    if (!shiftBatRow.stopId?.length || !shiftBatRow.stop) missing.push('stop');
    return missing?.length ? `Missing ${missing.join(', ')}` : null;
};

/**
 * Validation functions for ShiftBatDeadRow
 */

/**
 * Check if the dead row has start and end locations
 * @param {Object} shiftBatRow - The dead row
 * @returns {boolean} True if start and end are set
 */
export const hasStartAndEnd = (shiftBatRow) => {
    return (!!shiftBatRow.start && !!shiftBatRow.end);
};

/**
 * Check if the dead row has valid start and end stops
 * @param {Object} shiftBatRow - The dead row
 * @param {Object} allStops - All available stops
 * @returns {boolean} True if start and end stops are valid
 */
export const hasValidStartEndStops = (shiftBatRow, {allStops}) => {
    return (!!allStops[shiftBatRow.startStopId] && !!allStops[shiftBatRow.endStopId]);
};

/**
 * Main validation function for ShiftBatDeadRow
 * @param {Object} shiftBatRow - The dead row
 * @param {Object} allStops - All available stops
 * @param {number} rowIndex - The index of the current row
 * @returns {boolean} True if dead row is valid
 */
export const isValidDead = (shiftBatRow, {allStops}, rowIndex) => {
    // Bypass validation if row is not loaded
    if (!shiftBatRow.loaded) {
        return true;
    }
    
    // Must have a route
    if (!shiftBatRow.route) {
        return false;
    }

    if(shiftBatRow.route.routeId === '_') {
        return false;
    }

    // if(!shiftBatRow.route.routeId.includes('#') || !shiftBatRow.route.routeId.includes(shiftBatRow.route.routeName)) {
    //     return false;
    // }
    
    // Must have either start/end locations or valid start/end stops
    const hasStartEndLocations = hasStartAndEnd(shiftBatRow);
    const hasValidStops = hasValidStartEndStops(shiftBatRow, {allStops});
    
    return hasStartEndLocations || hasValidStops;
};

/**
 * Get the reason why a dead row is invalid
 * @param {Object} shiftBatRow - The dead row
 * @param {Object} allStops - All available stops
 * @param {number} rowIndex - The index of the current row
 * @returns {string} Reason for invalidity
 */
export const invalidReasonDead = (shiftBatRow, {allStops}, rowIndex) => {
    // First check basic validation - if that fails, return that reason
    const basicReason = invalidReason(shiftBatRow, rowIndex);
    if (basicReason) {
        return basicReason;
    }
    
    // Only check dead-specific issues if basic validation passes
    if (!allStops[shiftBatRow.startStopId] && !allStops[shiftBatRow.endStopId]) {
        return 'Start and end stops invalid';
    } else if (!allStops[shiftBatRow.startStopId]) {
        return 'Start stop invalid';
    } else if (!allStops[shiftBatRow.endStopId]) {
        return 'End stop invalid';
    } else if (!shiftBatRow.route) {
        return 'Route not found';
    } else if(!isValidDead(shiftBatRow, {allStops}, rowIndex)) {
        return 'Invalid Special';
    }
    
    return undefined; // No issues found
};

/**
 * Validation functions for ShiftBatNoteRow
 */

/**
 * Main validation function for ShiftBatNoteRow
 * @param {Object} shiftBatRow - The note row
 * @param {Object} args - Validation arguments
 * @param {number} rowIndex - The index of the current row
 * @returns {boolean} True if note row is valid
 */
export const isValidNote = (shiftBatRow, args, rowIndex) => {
    return shiftBatRow.type === 'Note' ? true : isValid(shiftBatRow, rowIndex);
};

/**
 * Validation functions for ShiftBatBreakRow
 */

/**
 * Check if the break row has a positive duration
 * @param {Object} shiftBatRow - The break row
 * @returns {boolean} True if duration is positive
 */
export const hasPositiveDuration = (shiftBatRow) => {
    return shiftBatRow.duration > 0;
};

/**
 * Main validation function for ShiftBatBreakRow
 * @param {Object} shiftBatRow - The break row
 * @param {Object} args - Validation arguments
 * @param {number} rowIndex - The index of the current row
 * @returns {boolean} True if break row is valid
 */
export const isValidBreak = (shiftBatRow, args, rowIndex) => {
    return !shiftBatRow.loaded || (hasPositiveDuration(shiftBatRow) && isValid(shiftBatRow, rowIndex));
};

/**
 * Get the reason why a break row is invalid
 * @param {Object} shiftBatRow - The break row
 * @param {Object} args - Validation arguments
 * @param {number} rowIndex - The index of the current row
 * @returns {string} Reason for invalidity
 */
export const invalidReasonBreak = (shiftBatRow, args, rowIndex) => {
    // First check basic validation - if that fails, return that reason
    const basicReason = invalidReason(shiftBatRow, rowIndex);
    if (basicReason) {
        return basicReason;
    }
    
    // Only check break-specific issues if basic validation passes
    if (shiftBatRow.duration <= 0) {
        return 'Break duration must be greater than 0';
    }
    
    return undefined; // No issues found
};

/**
 * Check if break row is in default state
 * @param {Object} shiftBatRow - The break row
 * @returns {boolean} True if row is in default state
 */
export const isDefaultBreak = (shiftBatRow) => {
    return isDefault(shiftBatRow) && !shiftBatRow.duration;
};

/**
 * Validation functions for ShiftBatCharterRouteRow
 */

/**
 * Check if the charter route row has valid start and end stops
 * @param {Object} shiftBatRow - The charter route row
 * @param {Object} allStops - All available stops
 * @returns {boolean} True if start and end stops are valid
 */
export const hasValidStartEndStopsCharter = (shiftBatRow, {allStops}) => {
    return allStops[shiftBatRow.startStopId] && allStops[shiftBatRow.endStopId];
};

/**
 * Main validation function for ShiftBatCharterRouteRow
 * @param {Object} shiftBatRow - The charter route row
 * @param {Object} allStops - All available stops
 * @param {number} rowIndex - The index of the current row
 * @returns {boolean} True if charter route row is valid
 */
export const isValidCharterRoute = (shiftBatRow, {allStops}, rowIndex) => {
    return !shiftBatRow.loaded || (hasValidStartEndStopsCharter(shiftBatRow, {allStops}) && isValid(shiftBatRow, rowIndex));
};

/**
 * Get the reason why a charter route row is invalid
 * @param {Object} shiftBatRow - The charter route row
 * @param {Object} allStops - All available stops
 * @param {number} rowIndex - The index of the current row
 * @returns {string} Reason for invalidity
 */
export const invalidReasonCharterRoute = (shiftBatRow, {allStops}, rowIndex) => {
    // First check basic validation - if that fails, return that reason
    const basicReason = invalidReason(shiftBatRow, rowIndex);
    if (basicReason) {
        return basicReason;
    }
    
    // Only check charter-specific issues if basic validation passes
    if (!allStops[shiftBatRow.startStopId] && !allStops[shiftBatRow.endStopId]) {
        return 'Start and end stops invalid';
    } else if (!allStops[shiftBatRow.startStopId]) {
        return 'Start stop invalid';
    } else if (!allStops[shiftBatRow.endStopId]) {
        return 'End stop invalid';
    }
    
    return undefined; // No issues found
};

/**
 * Validation functions for ShiftBatTemplateRow
 */

/**
 * Main validation function for ShiftBatTemplateRow
 * @param {Object} shiftBatRow - The template row
 * @param {Object} args - Validation arguments
 * @param {number} rowIndex - The index of the current row
 * @returns {boolean} True if template row is valid
 */
export const isValidTemplate = (shiftBatRow, args, rowIndex) => {
    return isValid(shiftBatRow, rowIndex);
};

/**
 * Check if this row doesn't have enough time to reach the next row's start time
 * @param {Object} shiftBatRow - The current shift bat row
 * @param {number} rowIndex - The index of the current row
 * @returns {boolean} True if this row causes an overlap with the next row
 */
export const causesNextOverlap = (shiftBatRow, rowIndex) => {
    try {
        // Only check for non-note rows
        if ([ShiftBatRowType.note].includes(shiftBatRow.type)) {
            return false;
        }
        
        const rows = shiftBatRow?.shiftBat?.rows || [];
        let nextRow = null;
        
        // Single loop: find next row AND calculate intermediate durations
        for(let i = rowIndex + 1; i < rows.length; i++){
            const candidateRow = rows[i];
            if (!candidateRow) continue;
            
            // Check if this is a row with timing information
            if([ShiftBatRowType.service, ShiftBatRowType.stop, ShiftBatRowType.noteTimed, ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken, ShiftBatRowType.dead].includes(candidateRow.type)){
                // We found the next row - stop here
                nextRow = candidateRow;
                break;
            }
        }
        
        if (!nextRow) return false;
        
        const nextTime = nextRow?.getTime?.();
        const thisEnd = shiftBatRow?.getEndTime?.();
        
        if (!Number.isFinite(nextTime) || !Number.isFinite(thisEnd)) return false;
        
        // Check if there's insufficient time (allow buffer time)
        return (nextTime - thisEnd) < 0;
    } catch (e) {
        return false;
    }
};

/**
 * Check if this row is affected by the previous row's overlap
 * @param {Object} shiftBatRow - The current shift bat row
 * @param {number} rowIndex - The index of the current row
 * @param {number} bufferTime - Buffer time in seconds (default: 120)
 * @returns {boolean} True if this row is affected by previous overlap
 */
export const isAffectedByPreviousOverlap = (shiftBatRow, rowIndex, bufferTime = 120) => {
    try {
        // Check for stop, break, and service rows (trips can also be affected by overlaps)
        if (![ShiftBatRowType.stop, ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken, ShiftBatRowType.service].includes(shiftBatRow.type)) {
            return false;
        }
        
        if (rowIndex <= 0) return false;
        
        const rows = shiftBatRow?.shiftBat?.rows || [];
        let prevRow = null;
        let timeNeeded = 0;
        
        // Find the previous row with timing information
        for(let i = rowIndex - 1; i >= 0; i--){
            const candidateRow = rows[i];
            if (!candidateRow) continue;
            
            if([ShiftBatRowType.dead, ShiftBatRowType.service, ShiftBatRowType.stop, ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken].includes(candidateRow.type)){
                prevRow = candidateRow;
                break;
            }

            timeNeeded += candidateRow.getDuration?.() || candidateRow.duration || 0;
        }
        
        if (!prevRow) return false;
        
        const prevEndTime = prevRow?.getEndTime?.();
        const thisTime = shiftBatRow?.getTime?.();
        
        if (!Number.isFinite(prevEndTime) || !Number.isFinite(thisTime)) return false;
        
        // Check if there's insufficient time (no buffer time for consistency with causesNextOverlap)
        return (thisTime - prevEndTime) < timeNeeded;
    } catch (e) {
        return false;
    }
};

/**
 * Check if dead run row should be highlighted due to insufficient time
 * @param {Object} shiftBatRow - The current shift bat row
 * @param {number} rowIndex - The index of the current row
 * @param {number} bufferTime - Buffer time in seconds (default: 120)
 * @returns {boolean} True if dead run timing is invalid
 */
export const deadRunTimeInvalid = (shiftBatRow, rowIndex, bufferTime = 120) => {
    try {
        if (shiftBatRow.type !== ShiftBatRowType.dead) return false;
        
        const rows = shiftBatRow?.shiftBat?.rows || [];
        let prevRow = null;
        let nextRow = null;
        
        // Find the previous row with timing information
        for(let i = rowIndex - 1; i >= 0; i--){
            const candidateRow = rows[i];
            if (!candidateRow) continue;
            
            if([ShiftBatRowType.dead, ShiftBatRowType.service, ShiftBatRowType.stop, ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken].includes(candidateRow.type)){
                prevRow = candidateRow;
                break;
            }
        }
        
        // Find the next row with timing information
        for(let i = rowIndex + 1; i < rows.length; i++){
            const candidateRow = rows[i];
            if (!candidateRow) continue;
            
            if([ShiftBatRowType.dead, ShiftBatRowType.service, ShiftBatRowType.stop, ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken].includes(candidateRow.type)){
                nextRow = candidateRow;
                break;
            }
        }
        
        if (!prevRow || !nextRow) return false;
        
        const prevEndTime = prevRow?.getEndTime?.();
        const nextStartTime = nextRow?.getTime?.();
        
        if (!Number.isFinite(prevEndTime) || !Number.isFinite(nextStartTime)) return false;
        
        // Check if there's insufficient time between previous row's end and next row's start
        return (nextStartTime - prevEndTime) < bufferTime;
    } catch (e) {
        return false;
    }
};

/**
 * Generate tooltip for time validation issues
 * @param {Object} shiftBatRow - The current shift bat row
 * @param {number} rowIndex - The index of the current row
 * @param {boolean} isAffectedByPreviousOverlap - Whether the row is affected by previous overlap
 * @returns {string} Tooltip message or empty string
 */
export const generateTimeTooltip = (shiftBatRow, rowIndex, isAffectedByPreviousOverlap) => {
    // Show time validation for stop, break, and service rows (trips can also have timing issues)
    if (![ShiftBatRowType.stop, ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken, ShiftBatRowType.service].includes(shiftBatRow.type)) {
        return '';
    }
    
    if (!isAffectedByPreviousOverlap) return '';
    
    const rows = shiftBatRow?.shiftBat?.rows || [];
    let prevRow = null;
    
    // Find the previous row with timing information
    for(let i = rowIndex - 1; i >= 0; i--){
        const candidateRow = rows[i];
        if (!candidateRow) continue;
        
        if([ShiftBatRowType.dead, ShiftBatRowType.service, ShiftBatRowType.stop, ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken].includes(candidateRow.type)){
            prevRow = candidateRow;
            break;
        }
    }
    
    if (!prevRow) return '';
    
    if (prevRow.type === ShiftBatRowType.service) {
        return 'Shift trip interlining time overlap issue';
    } else if (prevRow.type === ShiftBatRowType.dead) {
        return 'Shift trip interlining time overlap issue';
    } else if (prevRow.type === ShiftBatRowType.stop) {
        return 'Not enough time after previous stop';
    } else if ([ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken].includes(prevRow.type)) {
        return 'Not enough time after break';
    }
    
    return 'Not enough time after previous row';
};

/**
 * Generate tooltip for next overlap issues
 * @param {Object} shiftBatRow - The current shift bat row
 * @param {number} rowIndex - The index of the current row
 * @param {boolean} causesNextOverlap - Whether this row causes next overlap
 * @returns {string} Tooltip message or empty string
 */
export const generateNextOverlapTooltip = (shiftBatRow, rowIndex, causesNextOverlap) => {
    if (!causesNextOverlap) return '';
    
    const rows = shiftBatRow?.shiftBat?.rows || [];
    let nextRow = null;
    let timeNeeded = 0;
    
    // Single loop: find next row AND calculate intermediate durations
    for(let i = rowIndex + 1; i < rows.length; i++){
        const candidateRow = rows[i];
        if (!candidateRow) continue;
        
        // Check if this is a row with timing information
        if([ShiftBatRowType.service, ShiftBatRowType.stop, ShiftBatRowType.noteTimed, ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken].includes(candidateRow.type)){
            // We found the next row - stop here
            nextRow = candidateRow;
            break;
        }
        
        // This is an intermediate row - add its duration
        timeNeeded += candidateRow.getDuration?.() || candidateRow.duration || 0;
    }
    
    if (!nextRow) {
        // This is a bug - we have causesNextOverlap=true but no next row
        console.error('VALIDATION BUG: generateNextOverlapTooltip called with causesNextOverlap=true but no next row found', {
            rowIndex,
            rowType: shiftBatRow.type,
            rowTitle: shiftBatRow.title
        });
        return 'Timing issue detected';
    }
    
    const nextTime = nextRow?.getTime?.();
    const thisEnd = shiftBatRow?.getEndTime?.();
    
    if (!nextTime || !thisEnd) {
        // This is a bug - missing timing information
        console.error('VALIDATION BUG: generateNextOverlapTooltip missing timing information', {
            rowIndex,
            rowType: shiftBatRow.type,
            rowTitle: shiftBatRow.title,
            nextTime,
            thisEnd
        });
        return 'Timing issue detected';
    }
    
    const timeAvailable = nextTime - thisEnd;
    const totalTimeNeeded = timeNeeded + (shiftBatRow.getDuration?.() || shiftBatRow.duration || 0);
    
    if (timeAvailable < totalTimeNeeded) {
        return 'Not enough time for next row';
    }
    
    // If we reach here, there's no actual overlap issue
    // This is a bug - we have causesNextOverlap=true but no actual overlap
    console.error('VALIDATION BUG: generateNextOverlapTooltip called with causesNextOverlap=true but no actual overlap detected', {
        rowIndex,
        rowType: shiftBatRow.type,
        rowTitle: shiftBatRow.title,
        timeAvailable,
        totalTimeNeeded
    });
    return 'Timing issue detected';
};

/**
 * Generate tooltip for previous overlap issues
 * @param {Object} shiftBatRow - The current shift bat row
 * @param {number} rowIndex - The index of the current row
 * @param {boolean} isAffectedByPreviousOverlap - Whether the row is affected by previous overlap
 * @returns {string} Tooltip message or empty string
 */
export const generatePreviousOverlapTooltip = (shiftBatRow, rowIndex, isAffectedByPreviousOverlap) => {
    if (!isAffectedByPreviousOverlap) return '';
    
    const rows = shiftBatRow?.shiftBat?.rows || [];
    let prevRow = null;
    
    // Find the previous row with timing information
    for(let i = rowIndex - 1; i >= 0; i--){
        const candidateRow = rows[i];
        if (!candidateRow) continue;
        
        if([ShiftBatRowType.dead, ShiftBatRowType.service, ShiftBatRowType.stop, ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken].includes(candidateRow.type)){
            prevRow = candidateRow;
            break;
        }
    }
    
    if (!prevRow) {
        // This is a bug - we have isAffectedByPreviousOverlap=true but no previous row
        console.error('VALIDATION BUG: generatePreviousOverlapTooltip called with isAffectedByPreviousOverlap=true but no previous row found', {
            rowIndex,
            rowType: shiftBatRow.type,
            rowTitle: shiftBatRow.title
        });
        return 'Timing issue detected';
    }
    
    // Calculate the actual timing difference
    const prevEndTime = prevRow?.getEndTime?.();
    const thisTime = shiftBatRow?.getTime?.();
    
    if (!Number.isFinite(prevEndTime) || !Number.isFinite(thisTime)) {
        return 'Timing issue detected';
    }
    
    const timeDifference = thisTime - prevEndTime;
    const timeDifferenceMinutes = Math.round(timeDifference / 60);
    
    // Format the message with the calculated timing difference
    if (timeDifferenceMinutes < 0) {
        // Overlap (negative time difference)
        return `Shift timing issue ${timeDifferenceMinutes}min`;
    } else if (timeDifferenceMinutes < 2) {
        // Very tight timing (less than 2 minutes buffer)
        return `Shift timing issue +${timeDifferenceMinutes}min`;
    } else {
        // Sufficient time
        return 'Timing issue detected';
    }
};

/**
 * Generate tooltip for dead run timing issues
 * @param {Object} shiftBatRow - The current shift bat row
 * @param {number} rowIndex - The index of the current row
 * @param {boolean} deadRunTimeInvalid - Whether dead run timing is invalid
 * @returns {string} Tooltip message or empty string
 */
export const generateDeadRunTooltip = (shiftBatRow, rowIndex, deadRunTimeInvalid, bufferTime = 120) => {
    if (!deadRunTimeInvalid) return '';
    
    const rows = shiftBatRow?.shiftBat?.rows || [];
    let prevRow = null;
    let nextRow = null;
    
    // Find the previous row with timing information
    for(let i = rowIndex - 1; i >= 0; i--){
        const candidateRow = rows[i];
        if (!candidateRow) continue;
        
        if([ShiftBatRowType.dead, ShiftBatRowType.service, ShiftBatRowType.stop, ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken].includes(candidateRow.type)){
            prevRow = candidateRow;
            break;
        }
    }
    
    // Find the next row with timing information
    for(let i = rowIndex + 1; i < rows.length; i++){
        const candidateRow = rows[i];
        if (!candidateRow) continue;
        
        if([ShiftBatRowType.dead, ShiftBatRowType.service, ShiftBatRowType.stop, ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken].includes(candidateRow.type)){
            nextRow = candidateRow;
            break;
        }
    }
    
    if (!prevRow || !nextRow) {
        // This is a bug - we have deadRunTimeInvalid=true but missing row information
        console.error('VALIDATION BUG: generateDeadRunTooltip called with deadRunTimeInvalid=true but missing row information', {
            rowIndex,
            rowType: shiftBatRow.type,
            rowTitle: shiftBatRow.title,
            hasPrevRow: !!prevRow,
            hasNextRow: !!nextRow
        });
        return 'Timing issue detected';
    }
    
    // Calculate the actual time gap to show in the message
    const prevEndTime = prevRow?.getEndTime?.();
    const nextStartTime = nextRow?.getTime?.();
    
    if (Number.isFinite(prevEndTime) && Number.isFinite(nextStartTime)) {
        const timeGap = nextStartTime - prevEndTime;
        
        // Only flag actual overlaps (negative gaps) - respect scheduler's timing decisions
        if (timeGap < 0) {
            return `Services overlap - no time gap between rows`;
        }
        // If timeGap >= 0, the scheduler has determined this is operationally correct
        return '';
    }
    
    return '';
};

/**
 * Get comprehensive validation for a specific row
 * @param {Object} shiftBatRow - The current shift bat row
 * @param {number} rowIndex - The index of the current row
 * @param {Object} options - Additional options for validation
 * @returns {Object} Complete validation state and tooltips
 */
export const getRowValidation = (shiftBatRow, rowIndex, options = {}) => {
    const { bufferTime = 120, allStops = {}, allRoutes = {} } = options;
    
    const causesNextOverlapResult = causesNextOverlap(shiftBatRow, rowIndex);
    const isAffectedByPreviousOverlapResult = isAffectedByPreviousOverlap(shiftBatRow, rowIndex, bufferTime);
    const deadRunTimeInvalidResult = deadRunTimeInvalid(shiftBatRow, rowIndex, bufferTime);
    
    const rowInvalid = !isValid(shiftBatRow, rowIndex);
    
    // Check if the row has type-specific validation issues
    let typeSpecificInvalid = false;
    let typeSpecificTooltip = '';
    
    if (shiftBatRow.type === ShiftBatRowType.service) {
        typeSpecificInvalid = !isValidService(shiftBatRow, {allRoutes}, rowIndex);
        typeSpecificTooltip = typeSpecificInvalid ? invalidReasonService(shiftBatRow, {allRoutes}, rowIndex) : '';
    } else if (shiftBatRow.type === ShiftBatRowType.stop) {
        typeSpecificInvalid = !isValidStop(shiftBatRow, {allStops}, rowIndex);
        typeSpecificTooltip = typeSpecificInvalid ? invalidReasonStop(shiftBatRow, {allStops}, rowIndex) : '';
    } else if (shiftBatRow.type === ShiftBatRowType.dead) {
        typeSpecificInvalid = !isValidDead(shiftBatRow, {allStops}, rowIndex);
        typeSpecificTooltip = typeSpecificInvalid ? invalidReasonDead(shiftBatRow, {allStops}, rowIndex) : '';
    } else if ([ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken].includes(shiftBatRow.type)) {
        typeSpecificInvalid = !isValidBreak(shiftBatRow, {}, rowIndex);
        typeSpecificTooltip = typeSpecificInvalid ? invalidReasonBreak(shiftBatRow, {}, rowIndex) : '';
    } else if (shiftBatRow.type === ShiftBatRowType.charterRoute) {
        typeSpecificInvalid = !isValidCharterRoute(shiftBatRow, {allStops}, rowIndex);
        typeSpecificTooltip = typeSpecificInvalid ? invalidReasonCharterRoute(shiftBatRow, {allStops}, rowIndex) : '';
    }
    
    // Generate tooltips for overlap validation issues
    const timeTooltip = generateTimeTooltip(shiftBatRow, rowIndex, isAffectedByPreviousOverlapResult);
    // const nextOverlapTooltip = generateNextOverlapTooltip(shiftBatRow, rowIndex, causesNextOverlapResult);
    const previousOverlapTooltip = generatePreviousOverlapTooltip(shiftBatRow, rowIndex, isAffectedByPreviousOverlapResult);
    const deadRunTooltip = generateDeadRunTooltip(shiftBatRow, rowIndex, deadRunTimeInvalidResult, bufferTime);
    
    // Get the primary validation message
    const getPrimaryValidationMessage = () => {
        // Priority order: Only show validation on the row that's affected, not the row that causes it
        if (isAffectedByPreviousOverlapResult) return previousOverlapTooltip;
        // TODO: these needs to be put back for the scenario where the previous stop time is causing the dearun to aarive after the next row's time
        // if (causesNextOverlapResult) return nextOverlapTooltip;
        if (deadRunTimeInvalidResult) return deadRunTooltip;
        if (typeSpecificInvalid) return typeSpecificTooltip;
        if (rowInvalid) return invalidReason(shiftBatRow, rowIndex);
        return '';
    };
    
    const primaryMessage = getPrimaryValidationMessage();
    
    // CRITICAL BUSINESS RULE: Every validation icon MUST have a message
    // If there's no message configured, this is a bug that needs immediate attention
    const hasValidationIssues = isAffectedByPreviousOverlapResult || deadRunTimeInvalidResult || 
                          rowInvalid || typeSpecificInvalid;
    if (hasValidationIssues && !primaryMessage) {
        // This is a bug - log it for immediate investigation
        console.error('VALIDATION BUG: Row has validation issues but no message configured', {
            rowIndex,
            rowType: shiftBatRow.type,
            rowTitle: shiftBatRow.title,
            causesNextOverlap: causesNextOverlapResult,
            isAffectedByPreviousOverlap: isAffectedByPreviousOverlapResult,
            deadRunTimeInvalid: deadRunTimeInvalidResult,
            rowInvalid,
            typeSpecificInvalid,
            typeSpecificTooltip,
            // nextOverlapTooltip,
            previousOverlapTooltip,
            deadRunTooltip,
            invalidReason: invalidReason(shiftBatRow, rowIndex)
        });
        
        // Log the fallback message but return the boolean
        console.warn('Using fallback message:', 'Validation issue detected (message not configured)');
    }

    // Ensure validationMessage is never empty when there are validation issues
    const finalValidationMessage = hasValidationIssues && !primaryMessage 
        ? 'Validation issue detected (message not configured)' 
        : primaryMessage;

    const errors = {
        title: shiftBatRow.isValidTitle ? !shiftBatRow.isValidTitle() : false,
        time: (shiftBatRow.isValidTime && shiftBatRow.isValidSequence) ? !(shiftBatRow.isValidTime() && shiftBatRow.isValidSequence(rowIndex)) : false
    };
    console.log('errors before type (',shiftBatRow.type,') specific: ', errors)
    switch (shiftBatRow.type) {
        case ShiftBatRowType.service:
            errors.route = shiftBatRow.isValidRoute ? !shiftBatRow.isValidRoute({allRoutes}) : false;
            errors.trip = shiftBatRow.isValidTrip ? !shiftBatRow.isValidTrip({allRoutes}) : false;
            delete errors.title;
            delete errors.time;
            break;
        case ShiftBatRowType.stop:
            errors.stop = shiftBatRow.hasValidStop ? !shiftBatRow.hasValidStop({allStops}) : false;
            delete errors.title;
            break;
        case ShiftBatRowType.dead:
            errors.route = !(shiftBatRow?.route?.waypoints?.length);
            delete errors.time;
            delete errors.title;
            break;
        case ShiftBatRowType.break:
        case ShiftBatRowType.breakMeal:
        case ShiftBatRowType.breakBroken:
            errors.duration = shiftBatRow.hasPositiveDuration ? !shiftBatRow.hasPositiveDuration() : false;
            break;
        default:
            break;
    }
    
    return {
        // Overlap validation states
        causesNextOverlap: causesNextOverlapResult,
        isAffectedByPreviousOverlap: isAffectedByPreviousOverlapResult,
        deadRunTimeInvalid: deadRunTimeInvalidResult,
        
        // Row validation state
        invalid: rowInvalid || typeSpecificInvalid,

        fieldErrors: errors,
        
        // Primary validation message - NEVER empty when there are validation issues
        validationMessage: finalValidationMessage,
        
        // Overlap tooltips
        timeTooltip,
        // nextOverlapTooltip,
        previousOverlapTooltip,
        deadRunTooltip,
        
        // Helper functions
        hasValidationIssues,
        shouldShowRedTime: () => {
            if (rowIndex === 0) return false;
            if (shiftBatRow.type === ShiftBatRowType.dead) return false;
            
            return (rowInvalid || typeSpecificInvalid || isAffectedByPreviousOverlapResult || deadRunTimeInvalidResult) && 
                   [ShiftBatRowType.stop, ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken, ShiftBatRowType.service].includes(shiftBatRow.type);
        },
        shouldShowRedBackground: () => {
            if (rowIndex === 0) return false;
            
            return ((isAffectedByPreviousOverlapResult || deadRunTimeInvalidResult) && 
                    [ShiftBatRowType.stop, ShiftBatRowType.break, ShiftBatRowType.breakMeal, ShiftBatRowType.breakBroken, ShiftBatRowType.service].includes(shiftBatRow.type)) ||
                   deadRunTimeInvalidResult || rowInvalid || typeSpecificInvalid;
        }
    };
};
